// Cinematic scroll hero (adapted from the "Mostar city" scroll story), plain React + CSS variables.
// A sticky stage under the header plays a scroll-scrubbed sequence over ~3700px:
//   1. wordmark rises and fades, intro text + category pills sink and fade
//   2. the parachute lifts away, the two cloud banks part, a product photo fades in behind,
//      the sky blurs under a light shade and the "offers" story panel slides through
//   3. the "about" story panel slides through
//   4. an endless product carousel flies in, then its arrow buttons appear
// Timings/maths follow the source prompt exactly (segment ranges, smoothing 0.14, pointer 0.12).
// Everything shown comes from Shopify data and Store settings. Reduced motion: no inertia, no
// pointer parallax (values snap to the scroll position).
(function () {
  const { useEffect, useRef, useState } = React;
  const h = React.createElement;

  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
  const smoothstep = (e0, e1, v) => { const x = clamp((v - e0) / (e1 - e0)); return x * x * (3 - 2 * x); };
  const lerp = (a, b, t) => a + (b - a) * t;
  const segmentInOut = (s, a, b, c, d) => { const enter = smoothstep(a, b, s), exit = smoothstep(c, d, s); return { enter, exit, active: enter * (1 - exit) }; };
  const SCROLL = 3700;

  // Cloud bank used for the two "split frame" layers (left one is mirrored in CSS).
  function CloudBank(props) {
    const mirror = props.mirror; props = Object.assign({}, props); delete props.mirror;
    return h("svg", Object.assign({ viewBox: "0 0 1200 520", preserveAspectRatio: "xMidYMax slice", "aria-hidden": "true", focusable: "false" }, props),
      h("g", { transform: mirror ? "translate(1200 0) scale(-1 1)" : undefined },
      h("g", { fill: "#ffffff" },
        h("ellipse", { cx: 150, cy: 420, rx: 260, ry: 170 }),
        h("ellipse", { cx: 420, cy: 380, rx: 250, ry: 190 }),
        h("ellipse", { cx: 700, cy: 440, rx: 280, ry: 160 }),
        h("ellipse", { cx: 300, cy: 250, rx: 170, ry: 130 }),
        h("ellipse", { cx: 560, cy: 270, rx: 150, ry: 115 }),
        h("rect", { x: -20, y: 430, width: 1000, height: 120 })
      ),
      h("g", { fill: "#eef3f8" },
        h("ellipse", { cx: 240, cy: 470, rx: 220, ry: 90 }),
        h("ellipse", { cx: 620, cy: 490, rx: 240, ry: 80 })
      ))
    );
  }

  function PtCinemaHero({ onNav, children }) {
    const root = useRef(null), stage = useRef(null), track = useRef(null), controls = useRef(null);
    const D = window.DATA || {}, S = window.PT_SETTINGS || {};
    const cats = (D.CATEGORIES || []).filter((c) => c.id !== "under-500");

    // Carousel items: up to 8 products across the menu categories (bestsellers first).
    const seen = {}, items = [];
    const push = (p, cat) => { if (!p || seen[p.id] || items.length >= 8) return; seen[p.id] = 1; items.push({ p, cat }); };
    ((D.PRODUCTS || {}).bestseller || []).forEach((p) => push(p, cats.find((c) => c.id === p._cat) || cats[0]));
    cats.forEach((c) => ((D.PRODUCTS || {})[c.id] || []).forEach((p) => push(p, c)));
    const n = items.length;
    const [active, setActive] = useState(n);        // start in the middle of 3 cloned sets
    const [jumping, setJumping] = useState(false);

    const offer = ptOffer(0, 0);
    const bq = Number(S.bundleQty) || 0, bp = Number(S.bundlePct) || 0;
    const photo = S.cineImage || (cats[3] && cats[3].img) || (cats[0] && cats[0].img) || "";
    const intro = S.shopDescription || "Calm, considered essentials for dogs and cats, designed in Mumbai and made for everyday walks, play and grooming.";

    useEffect(() => {
      const el = root.current, st = stage.current;
      if (!el || !st) return;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
      let tMX = 0, tMY = 0, mX = 0, mY = 0, target = 0, smooth = 0, init = false, pending = false;
      const set = (k, v) => el.style.setProperty(k, v);
      const navH = () => { const nav = document.querySelector(".nav"); return nav ? Math.round(nav.getBoundingClientRect().height) : 0; };
      const layout = () => { set("--cine-top", navH() + "px"); };
      const distance = () => clamp((parseFloat(getComputedStyle(st).top) || 0) - el.getBoundingClientRect().top, 0, el.offsetHeight - st.offsetHeight);

      const update = () => {
        pending = false;
        target = distance();
        if (!init || reduce.matches) { smooth = target; init = true; } else { smooth = lerp(smooth, target, 0.14); }
        if (Math.abs(smooth - target) < 0.08) smooth = target;
        mX = lerp(mX, tMX, 0.12); mY = lerp(mY, tMY, 0.12);
        const px = reduce.matches ? 0 : mX, py = reduce.matches ? 0 : mY;
        const vh = st.offsetHeight;

        const frame2 = segmentInOut(smooth, 560, 900, 1300, 1620);
        const frame3 = segmentInOut(smooth, 1760, 2140, 2540, 2700);
        const progress = clamp(smooth / 2700);
        const introExit = smoothstep(90, 650, smooth);
        const cardsEnter = Math.pow(smoothstep(2760, 3560, smooth), 1.55);
        const controlsEnter = smoothstep(3360, 3660, smooth);
        const blurActive = clamp(frame2.active + frame3.active);
        const splitDrift = Math.pow(frame2.enter, 1.5);
        const backScale = 0.76 + progress * 0.2 + frame2.enter * 0.18 + frame3.enter * 0.16;
        const heroY = progress * -74, heroScale = progress * 0.23;
        const cardsTop = Math.min(220, Math.max(112, vh * 0.19)) - 50;

        // Back (sky + small clouds): starts at 1 on screen, then keeps zooming like the source back stack.
        set("--back-x", (px * -12).toFixed(2) + "px");
        set("--back-y", (py * -4).toFixed(2) + "px");
        set("--back-scale", (backScale / 0.76).toFixed(4));
        set("--blur-px", (blurActive * 14).toFixed(2) + "px");
        set("--back-brightness", (1 - blurActive * 0.255).toFixed(4));
        set("--shade-a1", (blurActive * 0.465).toFixed(4));
        set("--shade-a2", (blurActive * 0.42).toFixed(4));
        set("--shade-a3", (blurActive * 0.51).toFixed(4));
        set("--title-y", (introExit * -210).toFixed(2) + "px");
        set("--title-scale", (1 - introExit * 0.08).toFixed(4));
        set("--title-opacity", (1 - introExit).toFixed(4));
        set("--chute-x", (px * 18).toFixed(2) + "px");
        set("--chute-y", (py * 8 + heroY - frame2.exit * 760).toFixed(2) + "px");
        set("--chute-scale", (1.02 + heroScale + frame2.exit * 0.46).toFixed(4));
        set("--split-l-x", (-splitDrift * 64).toFixed(3) + "vw");
        set("--split-r-x", (splitDrift * 64).toFixed(3) + "vw");
        set("--split-mx", (px * 22).toFixed(2) + "px");
        set("--split-y", (py * 10 + heroY - splitDrift * 180).toFixed(2) + "px");
        set("--split-opacity", (1 - clamp(splitDrift * 1.25)).toFixed(4));
        set("--split-scale", (1 + heroScale + frame2.enter * 0.74).toFixed(4));
        set("--frame2-opacity", (frame2.active * (1 - frame3.enter)).toFixed(4));
        set("--frame2-x", (px * 10).toFixed(2) + "px");
        set("--frame2-y", (py * 8 - frame2.exit * 150).toFixed(2) + "px");
        set("--frame2-scale", (1.06 + frame2.enter * 0.08 + frame2.exit * 0.08).toFixed(4));
        set("--intro-y", (introExit * 90).toFixed(2) + "px");
        set("--intro-opacity", (1 - introExit).toFixed(4));
        set("--panel2-opacity", (frame2.active * (1 - frame2.exit)).toFixed(4));
        set("--panel2-y", (-frame2.exit * 86 + (1 - frame2.enter) * 58).toFixed(2) + "px");
        set("--panel3-opacity", (frame3.active * (1 - frame3.exit)).toFixed(4));
        set("--panel3-y", (-frame3.exit * 86 + (1 - frame3.enter) * 58).toFixed(2) + "px");
        set("--cards-visibility", cardsEnter > 0.01 ? "visible" : "hidden");
        set("--cards-x", ((1 - cardsEnter) * 420).toFixed(3) + "vw");
        set("--cards-top", cardsTop.toFixed(2) + "px");
        set("--controls-opacity", controlsEnter.toFixed(4));
        el.classList.toggle("controls-ready", controlsEnter > 0.98);
        el.classList.toggle("intro-gone", introExit > 0.98);
        el.classList.toggle("panel2-on", frame2.active * (1 - frame2.exit) > 0.5);
        el.classList.toggle("panel3-on", frame3.active * (1 - frame3.exit) > 0.5);

        if (Math.abs(smooth - target) > 0.08 || Math.abs(mX - tMX) > 0.001 || Math.abs(mY - tMY) > 0.001) tick();
      };
      const tick = () => { if (!pending) { pending = true; requestAnimationFrame(update); } };
      const onMove = (e) => { tMX = e.clientX / window.innerWidth - 0.5; tMY = e.clientY / window.innerHeight - 0.5; tick(); };
      const onResize = () => { layout(); tick(); };
      layout(); tick();
      window.addEventListener("scroll", tick, { passive: true });
      window.addEventListener("resize", onResize);
      window.addEventListener("pointermove", onMove, { passive: true });
      return () => { window.removeEventListener("scroll", tick); window.removeEventListener("resize", onResize); window.removeEventListener("pointermove", onMove); };
    }, []);

    // Carousel position: shift by whole cards; loop via 3 cloned sets and an instant jump.
    useEffect(() => {
      const t = track.current; if (!t || !n) return;
      const card = t.querySelector(".cine-card"); if (!card) return;
      const gap = parseFloat(getComputedStyle(t).columnGap || "0") || 0;
      t.style.setProperty("--cards-shift", (-(card.offsetWidth + gap) * active) + "px");
    });
    const onTrackEnd = () => {
      if (active >= n * 2) jump(active - n); else if (active < n) jump(active + n);
    };
    const jump = (i) => { setJumping(true); setActive(i); requestAnimationFrame(() => requestAnimationFrame(() => setJumping(false))); };

    const all = [0, 1, 2].flatMap((set) => items.map((it, k) => ({ it, idx: set * n + k })));

    return h("div", { ref: root, className: "cine", style: { height: "calc(100vh + " + SCROLL + "px)" } },
      h("div", { ref: stage, className: "cine-stage" },
        h("div", { className: "hero-main cine-art" }, children),
        h(CloudBank, { className: "cine-split cine-split-left" }),
        h(CloudBank, { className: "cine-split cine-split-right", mirror: true }),
        photo ? h("img", Object.assign({ className: "cine-frame2", alt: "" }, ptImgProps(photo, [900, 1400, 2000], "100vw"), { loading: "lazy" })) : null,
        h("div", { className: "cine-shade", "aria-hidden": "true" }),
        h("section", { className: "cine-intro", "aria-label": "About the shop" },
          h("p", null, intro),
          h("div", { className: "cine-tags", "aria-label": "Shop categories" },
            cats.slice(0, 3).map((c) => h("button", { key: c.id, type: "button", tabIndex: undefined, onClick: () => onNav({ page: "category", id: c.id }) }, c.name)))
        ),
        h("section", { className: "cine-panel cine-panel-offer", "aria-label": "Delivery and offers" },
          h("h2", null, "Free delivery above " + ptInr(offer.freeAt) + "."),
          h("p", null, "Most orders are dispatched the same day."),
          h("dl", { className: "cine-facts" },
            h("div", null, h("dt", null, ptInr(offer.freeAt)), h("dd", null, "Free delivery above")),
            bq > 0 && bp > 0
              ? h("div", null, h("dt", null, bp + "%"), h("dd", null, "Off when you buy " + bq))
              : h("div", null, h("dt", null, "1\u20132"), h("dd", null, "Days to dispatch")))
        ),
        h("section", { className: "cine-panel cine-panel-about", "aria-label": "About ThePawtherapy" },
          h("h2", null, "Made for Indian pet parents."),
          h("p", null, "Grooming, walks, play and everyday essentials, designed in Mumbai and tested on real pets."),
          h("button", { type: "button", className: "cine-note", onClick: () => onNav({ page: "category", id: "all" }) },
            h("span", { "aria-hidden": "true" }, h(Icon.ArrowUpRight, null)), h("span", null, "Shop all products"))
        ),
        n ? h("section", { className: "cine-cards", "aria-label": "Featured products" },
          h("div", { ref: track, className: "cine-track" + (jumping ? " is-jumping" : ""), onTransitionEnd: (e) => { if (e.target === track.current) onTrackEnd(); } },
            all.map(({ it, idx }) => {
              const price = it.p.salePrice != null ? it.p.salePrice : it.p.price;
              return h("button", {
                key: idx, type: "button", className: "cine-card" + (idx === active ? " is-active" : ""),
                "aria-label": it.p.name + ", " + ptInr(price) + (idx === active ? "" : ""),
                tabIndex: idx >= n && idx < n * 2 ? 0 : -1,
                onClick: () => onNav({ page: "pdp", id: it.p.id, category: it.cat ? it.cat.id : "" }),
              },
                h("span", { className: "cine-kicker" }, it.cat ? it.cat.name : ""),
                it.p.img ? h("img", Object.assign({ className: "cine-pin", alt: "" }, ptImgProps(it.p.img, [150, 300], "68px"))) : null,
                h("span", { className: "cine-card-title" }, it.p.name),
                h("span", { className: "cine-card-text" }, ptInr(price) + (it.p.tagline ? " \u00B7 " + it.p.tagline : ""))
              );
            })
          )
        ) : null,
        n ? h("div", { ref: controls, className: "cine-controls", role: "group", "aria-label": "Carousel controls" },
          h("button", { type: "button", className: "cine-nav", "aria-label": "Previous product", onClick: () => setActive((a) => a - 1) }, "\u2190"),
          h("button", { type: "button", className: "cine-nav", "aria-label": "Next product", onClick: () => setActive((a) => a + 1) }, "\u2192")
        ) : null
      )
    );
  }

  window.PtCinemaHero = PtCinemaHero;
})();
